import { computed } from 'vue'
import { useRouter, type Router } from 'vue-router'
import { routes } from '@app/router/routes'
import {
  useAccountStatusQuery,
  useBankAccountsQuery,
  useReInitiateKycMutation,
  ACCOUNT_STATE,
  PAUSED_REASON,
} from '@modules/account'
import { CRYPTO_WALLET_STATUS, useFiatWalletQuery, useWalletStatusQuery } from '@modules/wallets'
import { useToastStore } from '@uiKit/store/toastStore'
import type { EligibilityFix } from '../components/InvestEligibilityBlocker.vue'
import { WALLET_STATUS_TOAST, WALLET_STATUS_TOAST_TYPE } from '../constants/walletStatusToast'

type EligibilityFixWithAction = EligibilityFix & { onCta: () => void }

interface FixContext {
  router: Router
  refetchWallet: () => void
  accessRestricted: boolean
  // Authoritative server-owned screen discriminator + paused cause. The KYC
  // gate below leans on these (not on kycStatus) because a re-KYC user keeps
  // kycStatus='KYC_COMPLETED' from their earlier verification.
  accountState: string
  pausedReason: string | null
  // Re-initiates KYC then routes into the resubmit wizard. Mirrors the
  // dashboard's StateReKycRequiredContainer handler.
  resubmitKyc: () => void
}

interface EligibilityReadiness {
  kycComplete: boolean
  riskProfileComplete: boolean
  bankComplete: boolean
  walletActive: boolean
}

// Paused account, any reason — terminal, no self-service path. Mirrors
// useDashboardState's exhaustive PAUSED_VARIANT map so buildFix can never
// silently fall through to "eligible" for a paused reason it doesn't know
// about yet (onboarding review rejected, suspended, deactivated, ...).
const buildPausedFix = (
  pausedReason: string | null,
  router: Router,
): EligibilityFixWithAction => {
  // Terminal KYC rejection gets its own copy (no self-service re-KYC).
  if (pausedReason === PAUSED_REASON.KYC_REJECTED) {
    return {
      icon: 'lock',
      title: 'Verification unsuccessful',
      description:
        "We couldn't verify your identity. Please contact support so we can help you continue.",
      ctaLabel: 'Contact support',
      onCta: () => router.push(routes.HELP),
    }
  }
  // Onboarding review rejected, suspended, or deactivated — same
  // contact-support path, generic copy since the dashboard already surfaces
  // the reason-specific screen.
  return {
    icon: 'lock',
    title: 'Account paused',
    description: 'Your account has been paused. Please contact support so we can help you continue.',
    ctaLabel: 'Contact support',
    onCta: () => router.push(routes.HELP),
  }
}

/**
 * Eligibility precedence: account restricted → re-KYC required → paused
 * (any reason) → KYC under review → KYC incomplete → risk profile → bank →
 * wallet. We surface the FIRST unmet step so the user makes one decision at
 * a time.
 */
const buildFix = (
  { kycComplete, riskProfileComplete, bankComplete, walletActive }: EligibilityReadiness,
  { router, refetchWallet, accessRestricted, accountState, pausedReason, resubmitKyc }: FixContext,
): EligibilityFixWithAction | null => {
  // Highest precedence: a restricted account can never invest, regardless of
  // KYC / bank / wallet readiness. Swap the eligibility step for a
  // contact-support blocker.
  if (accessRestricted) {
    return {
      icon: 'lock',
      title: 'Account access restricted',
      description:
        'Your account access has been restricted. Please contact support for more information.',
      ctaLabel: 'Contact support',
      onCta: () => router.push(routes.HELP),
    }
  }
  // Re-KYC required: the user's verification was rejected (admin re-initiate /
  // active re-verify) or expired. They cannot invest even with a verified bank
  // and an active wallet — the path forward is to resubmit, not to add a bank.
  if (accountState === ACCOUNT_STATE.REKYC_REJECTED) {
    return {
      icon: 'shield',
      title: 'Resubmit your KYC',
      description:
        'Your identity verification needs to be redone before you can invest. It only takes a couple of minutes.',
      ctaLabel: 'Resubmit KYC',
      onCta: resubmitKyc,
    }
  }
  if (accountState === ACCOUNT_STATE.REKYC_EXPIRED) {
    return {
      icon: 'shield',
      title: 'Renew your verification',
      description: 'Your identity verification has expired. Renew it to continue investing.',
      ctaLabel: 'Renew verification',
      onCta: resubmitKyc,
    }
  }
  if (accountState === ACCOUNT_STATE.PAUSED) {
    return buildPausedFix(pausedReason, router)
  }
  // Sumsub can flip kycStatus to KYC_COMPLETED as soon as the automated check
  // passes, independent of the admin's separate manual onboarding-review step —
  // so kycComplete alone can't be trusted while the account is still under
  // review. accountState is the authoritative, backend-owned discriminator.
  if (accountState === ACCOUNT_STATE.IN_REVIEW) {
    return {
      icon: 'eye',
      title: 'Your profile is under review',
      description:
        "Our team is reviewing your submission. You'll be notified once approved and can invest then.",
      ctaLabel: 'Go to dashboard',
      onCta: () => router.push(routes.DASHBOARD),
    }
  }
  if (kycComplete && riskProfileComplete && bankComplete && walletActive) return null
  if (!kycComplete) {
    return {
      icon: 'shield',
      title: 'Verify your identity',
      description: 'Complete KYC to start investing. It takes about 2 minutes.',
      ctaLabel: 'Start verification',
      onCta: () => router.push(routes.DASHBOARD),
    }
  }
  if (!riskProfileComplete) {
    return {
      icon: 'list-check',
      title: 'Complete your risk profile',
      description:
        'Answer a few questions about your investment goals so we can confirm this is right for you.',
      ctaLabel: 'Complete risk profile',
      onCta: () => router.push(routes.RISK_PROFILE),
    }
  }
  if (!bankComplete) {
    return {
      icon: 'building-columns',
      title: 'Add a verified bank account',
      description: 'Link a bank account so we can return your earnings.',
      ctaLabel: 'Add bank account',
      onCta: () => router.push(routes.BANK_ACCOUNTS),
    }
  }
  return {
    icon: 'wallet',
    title: 'Activating your wallet',
    description: 'Your platform wallet is being set up. Refresh in a moment.',
    ctaLabel: 'Refresh status',
    onCta: refetchWallet,
  }
}

/**
 * Derives whether the user can transact on this offering and — if not — the
 * single next blocker to surface. Wallet status is keyed by `propertyId` so the
 * trustline check is per-offering.
 */
export function useInvestEligibility(propertyId: string) {
  const router = useRouter()
  const accountStatusQuery = useAccountStatusQuery()
  const walletStatusQuery = useWalletStatusQuery(propertyId)
  const fiatWalletQuery = useFiatWalletQuery()
  const bankAccountsQuery = useBankAccountsQuery()
  const reInitiateKycMutation = useReInitiateKycMutation()
  const toastStore = useToastStore()

  // Refetch the latest wallet status and tell the user what it is — otherwise a
  // still-pending wallet looks like the button did nothing.
  const refreshWalletStatus = async () => {
    const { data, error } = await walletStatusQuery.refetch()
    if (error) {
      toastStore.addToast({
        type: 'error',
        message: error.message ?? WALLET_STATUS_TOAST.failed,
      })
      return
    }
    const status = data?.walletStatus
    if (!status) {
      toastStore.addToast({ type: 'info', message: WALLET_STATUS_TOAST.notFound })
      return
    }
    toastStore.addToast({
      type: WALLET_STATUS_TOAST_TYPE[status],
      message: WALLET_STATUS_TOAST[status],
    })
  }

  const kycComplete = computed(() => accountStatusQuery.data.value?.kycStatus === 'KYC_COMPLETED')
  const riskProfileComplete = computed(
    () => accountStatusQuery.data.value?.profileCompleted ?? false,
  )
  // Gate on the live bank list, not account-status's `bankAccount` snapshot —
  // bank add / delete mutations don't invalidate account-status, so a stale
  // snapshot let a user with no bank fall through to the wallet step. The
  // snapshot only bridges the list's first load.
  const bankComplete = computed(() => {
    const accounts = bankAccountsQuery.data.value
    if (accounts) return accounts.some((account) => account.accountStatus === 'verified')
    return accountStatusQuery.data.value?.bankAccount?.accountStatus === 'verified'
  })
  // Gate on the latest wallet's status only (mirrors the backend's order-placement
  // check, which doesn't require the trustline to be active yet).
  const walletActive = computed(
    () => walletStatusQuery.data.value?.walletStatus === CRYPTO_WALLET_STATUS.ACTIVE,
  )

  const accessRestricted = computed(() => accountStatusQuery.data.value?.accessRestricted ?? false)
  const accountState = computed(() => accountStatusQuery.data.value?.accountState ?? '')
  const pausedReason = computed(() => accountStatusQuery.data.value?.pausedReason ?? null)

  // Re-initiate KYC only on explicit CTA tap, then route into the resubmit
  // wizard — never on mount (that would flip the account to "under review"
  // before the user chooses to redo). Mirrors StateReKycRequiredContainer.
  const resubmitKyc = async () => {
    try {
      await reInitiateKycMutation.mutateAsync()
    } catch {
      return
    }
    router.push({ path: routes.ONBOARDING_WIZARD, query: { mode: 'resubmit' } })
  }

  const eligibilityFix = computed(() =>
    buildFix(
      {
        kycComplete: kycComplete.value,
        riskProfileComplete: riskProfileComplete.value,
        bankComplete: bankComplete.value,
        walletActive: walletActive.value,
      },
      {
        router,
        refetchWallet: refreshWalletStatus,
        accessRestricted: accessRestricted.value,
        accountState: accountState.value,
        pausedReason: pausedReason.value,
        resubmitKyc,
      },
    ),
  )
  // Eligible exactly when there's no blocker to surface — derive from the fix so
  // the two can never drift (a re-KYC user is blocked even though kyc/bank/
  // wallet booleans may all read "ready").
  const isEligible = computed(() => eligibilityFix.value === null)
  const walletBalance = computed(() => Number(fiatWalletQuery.data.value?.availableBalance ?? 0))

  return { isEligible, eligibilityFix, walletBalance }
}
